import Breadcrumb from "components/Common/Breadcrumb"
import { useEffect, useMemo, useState } from "react"
import { FaEdit, FaFileExcel, FaPlus, FaSave } from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Button,
  Card,
  CardBody,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import React from "react"
import Swal from "sweetalert2"
import * as XLSX from "xlsx"
import {
  loadContacts,
  normalizePhone,
  saveContacts,
  toLocal10,
} from "utils/waContacts"

const EMPTY_FORM = { contactName: "", phoneNumber: "" }

const WhatsAppContacts = () => {
  document.title = "WhatsApp Contacts | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState(() => loadContacts())
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const [fileKey, setFileKey] = useState(0)

  // TODO: gate with backend permission id when available (showing openly for now)

  // Keep Contacts & Groups pages in sync until backend APIs are ready
  useEffect(() => {
    saveContacts(data)
  }, [data])

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(
      item =>
        item?.contactName?.toLowerCase().includes(search) ||
        item?.phoneNumber?.toLowerCase().includes(search)
    )
  }, [data, searchString])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const openAddModal = () => {
    setSelectedContact(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setSelectedContact({ ...item, phoneNumber: toLocal10(item.phoneNumber) })
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedContact?.contactName?.trim()) {
      newErrors.contactName = "Contact name is required"
    }
    const digits = normalizePhone(selectedContact?.phoneNumber)
    if (!selectedContact?.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Phone number is required"
    } else if (!/^\d{10}$/.test(digits)) {
      newErrors.phoneNumber = "Enter a valid 10-digit mobile number"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Design-first: local CRUD only (no API yet)
  const handleSubmit = () => {
    if (!validate()) return

    const digits = normalizePhone(selectedContact.phoneNumber)
    const fullNumber = `+91${digits}`
    if (selectedContact?.id) {
      const duplicate = data.some(
        d =>
          d.id !== selectedContact.id &&
          toLocal10(d.phoneNumber) === digits
      )
      if (duplicate) {
        setErrors(prev => ({
          ...prev,
          phoneNumber: "This phone number already exists",
        }))
        return
      }
      setData(prev =>
        prev.map(item =>
          item.id === selectedContact.id
            ? {
                ...item,
                contactName: selectedContact.contactName.trim(),
                phoneNumber: fullNumber,
              }
            : item
        )
      )
      Swal.fire({
        title: "Contact Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      const duplicate = data.some(
        d => toLocal10(d.phoneNumber) === digits
      )
      if (duplicate) {
        setErrors(prev => ({
          ...prev,
          phoneNumber: "This phone number already exists",
        }))
        return
      }
      const newItem = {
        id: Date.now(),
        contactName: selectedContact.contactName.trim(),
        phoneNumber: fullNumber,
        createdAt: new Date().toISOString(),
      }
      setData(prev => [newItem, ...prev])
      Swal.fire({
        title: "Contact Added Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setModalOpen(false)
    setSelectedContact(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "whatsapp-contacts" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.contactName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => d.id !== item.id))
        Swal.fire({
          title: "Contact Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "whatsapp-contacts" when API is ready
      }
    })
  }

  const openImportModal = () => {
    setImportFile(null)
    setImportResult(null)
    setFileKey(prev => prev + 1)
    setImportModalOpen(true)
  }

  const closeImportModal = () => {
    setImportModalOpen(false)
    setImportFile(null)
    setImportResult(null)
  }

  const handleFileChange = e => {
    const file = e.target.files?.[0]
    setImportFile(file || null)
    setImportResult(null)
    if (!file) return
    const reader = new FileReader()
    reader.onload = evt => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" })
        let startIdx = 0
        if (rows.length) {
          const first = rows[0].map(c => String(c).trim().toLowerCase())
          if (first[0] === "name" && first[1] === "number") startIdx = 1
        }
        const valid = []
        const invalid = []
        rows.slice(startIdx).forEach((r, i) => {
          const name = String(r[0] ?? "").trim()
          const number = toLocal10(String(r[1] ?? ""))
          if (!name || !/^\d{10}$/.test(number)) {
            invalid.push(startIdx + i + 1)
          } else {
            valid.push({ name, number })
          }
        })
        setImportResult({ valid, invalid })
      } catch {
        setImportResult({
          valid: [],
          invalid: [],
          error: "Could not read this file. Please upload a valid Excel sheet.",
        })
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleImport = () => {
    const rows = importResult?.valid || []
    const existing = new Set(data.map(d => toLocal10(d.phoneNumber)))
    const seen = new Set()
    const toAdd = []
    let skipped = 0
    rows.forEach(r => {
      if (existing.has(r.number) || seen.has(r.number)) {
        skipped++
        return
      }
      seen.add(r.number)
      toAdd.push({
        id: `${Date.now()}-${r.number}`,
        contactName: r.name,
        phoneNumber: `+91${r.number}`,
        createdAt: new Date().toISOString(),
      })
    })
    if (!toAdd.length) {
      Swal.fire({
        title: "Nothing to import.",
        text: "All rows are either invalid or already exist.",
        icon: "warning",
      })
      return
    }
    setData(prev => [...toAdd, ...prev])
    closeImportModal()
    setCurrentPage(1)
    Swal.fire({
      title: `${toAdd.length} Contact(s) Imported Successfully.`,
      text:
        skipped > 0 ? `${skipped} duplicate row(s) skipped.` : undefined,
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    })
    // TODO: replace with FetchPostData "whatsapp-contacts/import" when API is ready
  }

  return (
    <div className="page-content">
      <Breadcrumb title="WhatsApp" breadcrumbItem="Contacts" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-nowrap">
            <div className="d-flex gap-2 align-items-center flex-nowrap flex-grow-1">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 pe-5 flex-shrink-0"
                style={{ width: "90px" }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4 flex-grow-1"
                style={{ maxWidth: "320px" }}
                placeholder="Search Contacts"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            <div className="d-flex gap-2 flex-shrink-0">
              <Button
                color="success"
                className="rounded-4 px-3 text-nowrap"
                onClick={openImportModal}
              >
                <FaFileExcel className="me-2" />
                Import
              </Button>
              <Button
                className="btn-blue rounded-4 px-3 text-nowrap"
                onClick={openAddModal}
              >
                <FaPlus className="me-2" />
                Contact
              </Button>
            </div>
          </div>

          {/* Table */}
          <Table bordered responsive className="align-middle">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: "70px" }}>
                  #
                </th>
                <th style={{ minWidth: "220px" }}>Contact Name</th>
                <th style={{ width: "220px" }}>Number</th>
                <th className="text-center" style={{ width: "140px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    No Contacts found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{start + index + 1}</td>
                    <td>
                      <span className="fw-bold">{item.contactName}</span>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      +91 {toLocal10(item.phoneNumber)}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        title="Edit contact"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit size={16} />
                      </Button>
                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        title="Delete contact"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedContact?.id ? "Edit" : "Add"} Contact
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>
              Contact Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="e.g. Rohit Sharma"
              value={selectedContact?.contactName || ""}
              invalid={!!errors.contactName}
              onChange={e =>
                setSelectedContact(prev => ({
                  ...prev,
                  contactName: e.target.value,
                }))
              }
            />
            {errors.contactName && (
              <FormFeedback className="d-block">
                {errors.contactName}
              </FormFeedback>
            )}
          </div>

          <div>
            <Label>
              Number <span className="text-danger">*</span>
            </Label>
            <InputGroup>
              <InputGroupText className="bg-light">+91</InputGroupText>
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="9876543210"
                value={selectedContact?.phoneNumber || ""}
                invalid={!!errors.phoneNumber}
                onChange={e =>
                  setSelectedContact(prev => ({
                    ...prev,
                    phoneNumber: e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10),
                  }))
                }
              />
            </InputGroup>
            {errors.phoneNumber && (
              <FormFeedback className="d-block">
                {errors.phoneNumber}
              </FormFeedback>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            <FaSave className="me-1" /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel className="me-1" /> Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Import contacts modal */}
      <Modal
        isOpen={importModalOpen}
        toggle={closeImportModal}
        centered
        size="lg"
      >
        <ModalHeader toggle={closeImportModal}>Import Contacts</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>
              Upload Contact Sheet <span className="text-danger">*</span>
            </Label>
            <Input
              key={fileKey}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
            />
            <small className="text-muted">
              Accepted formats: .xlsx, .xls, .csv
            </small>
          </div>

          <div>
            <Label className="fw-bold">Reference Sheet Format</Label>
            <Table bordered responsive className="align-middle mb-0">
              <thead className="table-dark">
                <tr>
                  <th>name</th>
                  <th>number</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Rahul Verma</td>
                  <td>9392004892</td>
                </tr>
              </tbody>
            </Table>
            <small className="text-muted">
              First row must be the header (name, number). Number must be
              10 digits.
            </small>
          </div>

          {importResult?.error && (
            <div className="alert alert-danger mt-3 mb-0">
              {importResult.error}
            </div>
          )}
          {importResult && !importResult.error && importFile && (
            <div className="mt-3">
              <div>
                <span className="fw-bold text-success">
                  {importResult.valid.length} valid row(s)
                </span>
                {importResult.invalid.length > 0 && (
                  <span className="text-danger">
                    {" "}
                    • {importResult.invalid.length} invalid row(s):{" "}
                    {importResult.invalid.join(", ")}
                  </span>
                )}
              </div>
              <small className="text-muted">
                Duplicate numbers already in contacts will be skipped.
              </small>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn-blue"
            disabled={!importResult?.valid?.length}
            onClick={handleImport}
          >
            <FaFileExcel className="me-1" /> Import
          </Button>
          <Button className="btn-danger" onClick={closeImportModal}>
            <MdCancel className="me-1" /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default WhatsAppContacts
